<template>
  <div class="chart" :layout="layout">
    <header v-if="lastDates || isPrintable" class="chart__time-header" v-html="headerContent" />
    <div ref="vizRoot" class="viz-root" :class="type" />
    <div ref="swipeDisclaimer" class="chart__swipe-disclaimer"><i />Drag left to see more</div>
    <!-- <div class="svg-btn" @click="downloadSVG">SVG</div> -->
    <footer class="print-layout">
      <section class="left">
        <h4>Notes</h4>
        <div class="disclaimers">
          <p v-for="(note, i) of disclaimers" :key="i" class="disclaimer">
            {{ note.value }}
          </p>
        </div>
        <h4>Source</h4>
        <p class="source">
          <span v-if="skinVersion == 1">OECD Going Digital Toolkit, </span>
          <span v-else>OECD Data Kitchen, </span>
          <span
            ><a target="_blank" :href="printURL">{{ printURL }}</a>
          </span>
          <span v-if="exportSources.length">, based on the {{ exportSources[0].value }}</span>
        </p>
      </section>
      <section class="right">
        <picture>
          <img class="logo datakitchen" src="@/assets/images/datakitchen__logo.png" />
          <h2>Data<br />Kitchen</h2>
        </picture>
        <picture v-if="![3, 4].includes(skinVersion)">
          <img class="logo goingdigital" src="@/assets/images/going-digital__icon.png" />
          <h2>OECD<br />Going Digital<br />Toolkit</h2>
        </picture>
        <img v-else class="logo external" :src="printLogoURL" />
      </section>
    </footer>
  </div>
</template>

<script>
import LollipopBarChartViz from './vizs/LollipopBarChartViz'
import LineChartViz from './vizs/LineChartViz'
import HeatMapViz from './vizs/HeatMapViz'
import ChoroplethViz from './vizs/ChoroplethViz'
import ScatterPlotViz from './vizs/ScatterPlotViz.js'
import { fontCheck } from './utils/fontUtils'
import { extractValues, mergeConfig } from './utils/configUtils'
import { colorPalettes, buildColorDict } from '@/utils/color.js'
import { flatten as flattenTree } from '@/utils/tree.js'
import { encodeDate } from '@/utils/dates.js'
import DisclaimersMixin from '@/mixins/disclaimers.js'
import isFlipped from '../../mixins/isFlipped'

export default {
  mixins: [DisclaimersMixin, isFlipped],
  model: {
    prop: 'focus',
    event: 'focus',
  },
  props: {
    focus: {
      default: null,
      type: String,
    },
    dataset: {
      required: true,
      // type: Array,
    },
    timeScale: {
      required: true,
      type: String,
    },
    timeInterval: {
      required: false,
      type: Array,
    },
    type: {
      required: true,
      type: String,
      validator: value =>
        ['barchart', 'linechart', 'heatmap', 'choropleth', 'scatterplot'].indexOf(value) !== -1,
    },
    selection: {
      type: Array,
      default: () => [],
    },
    evolutiveData: {
      type: Array,
    },
    units: {
      type: Object,
    },
  },
  computed: {
    data: {
      get() {
        if (this.type === 'linechart') {
          const broadbandFilter =
            /broadband/i.test(this.$store.state.query.mainCubeId) && this.timeScale === 'Q'
          return this.dataset.transposeCountries(broadbandFilter)
        } else return this.dataset
      },
    },
    lastDates() {
      return (
        this.$store.state.query.lastDates === true && !['linechart', 'heatmap'].includes(this.type)
      )
    },
    chartClassName() {
      if (this.type === 'barchart') return LollipopBarChartViz
      else if (this.type === 'linechart') return LineChartViz
      else if (this.type === 'heatmap') return HeatMapViz
      else if (this.type === 'choropleth') return ChoroplethViz
      else if (this.type === 'scatterplot') return ScatterPlotViz
      else return null
    },
    fontSize() {
      return this.$store.state.query.fontSize
    },
    palette() {
      return this.$store.state.query.palette
    },
    annotations() {
      return this.$store.getters.annotations
    },
    headerContent() {
      let timeInterval = this.timeInterval
      if (this.lastDates) {
        timeInterval = [
          this.data.slice(1).reduce((date, row) => {
            return Math.max(date, row[this.data.fields._DATE_].getTime())
          }, 0),
        ]
      }

      return `<p>${timeInterval.map(v => encodeDate(v)).join('-')}</p>`
    },
    breakdownDimensions() {
      return this.$store.state.cubeMeta.mainBreakdownDimensions || []
    },
    lollipopId() {
      return this.$store.state.query.lollipop || this.breakdownDimensions[0].id
    },
    lollipopDimension() {
      return this.breakdownDimensions.find(dim => dim.id === this.lollipopId)
    },
    lollipopOpts() {
      return this.lollipopDimension
        ? flattenTree(this.lollipopDimension.options).filter(opt =>
            this.$store.state.query.lollipopOpts.includes(opt.id)
          )
        : []
    },
    skinVersion() {
      return Number(this.$route.params.version)
    },
    printURL() {
      let path = this.$route.path.replace(/print/, 'explore')
      if (this.$router.mode === 'hash') {
        path = '/#' + path
      }
      return 'https://goingdigital.oecd.org/datakitchen' + path
    },
    printLogoURL() {
      switch (this.skinVersion) {
        case 3:
          return 'https://goingdigital-data.oecd.org/media/oecdorg/satellitesites/goingdigitaltoolkit/Data_Kitchen_Icon_TiVA.svg'
        case 4:
          return 'https://goingdigital-data.oecd.org/media/oecdorg/satellitesites/goingdigitaltoolkit/Data_Kitchen_Icon_BROADBAND.svg'
        default:
          return '@/assets/images/going-digital__logo.png'
      }
    },
    yDomain() {
      if (this.units && this.units.y?.id === '%') {
        const yMax = this.getRangeMax('y')
        if (yMax >= 90 && yMax <= 100 && this.type !== 'heatmap') {
          return ['auto', 100]
        }
      }
      if (this.isNDSC && this.type != 'heatmap') {
        return ['auto', 100]
      }
      return ['auto', 'auto']
    },
    isNDSC() {
      return /DF_NDSC$/i.test(this.$store.state.query.mainCubeId)
    },
    showFullLables() {
      return this.$store.state.query.fullLabel
    },
  },
  watch: {
    units(to) {
      this.viz.config.AxisHelper.y.unit = to.y?.label
    },
    data(to) {
      this.viz.config.AxisHelper.zoom =
        this.type === 'barchart' &&
        this.viz.$chartRoot.clientWidth / this.data.length < 20 &&
        !(this.isThumbnail || this.isPrintable)

      if (this.viz.config.AxisHelper.zoom) {
        this.$refs.swipeDisclaimer.classList.add('visible')
      } else {
        this.$refs.swipeDisclaimer.classList.remove('visible')
      }

      this.viz.config.AxisHelper.y.domain = this.yDomain
      this.viz.config.AxisHelper.y.valueFormat = this.valueFormat('y')
      this.viz.config.sizeAttr = to.sizeAttr
      this.viz.config.colorMode = this.palette
      this.updateChart()
    },
    evolutiveData(to) {
      this.viz.config.TooltipBehavior.evolutive = to
    },
    timeInterval(to, from) {
      if (String(to) === String(from)) return
      this.updateChart()
    },
    showFullLables(to) {
      if (this.type === 'barchart' || this.type === 'heatmap') {
        this.viz.config.AxisHelper.x.tickOrientation = to ? 'vertical' : 'skewed'
        this.viz.config.AxisHelper.x.customWrapMax = to ? 120 : null
        this.updateChart()
      }
    },
    fontSize(to, from) {
      if (to === from) return
      this.viz.config.AxisHelper.fontSize = to
      this.viz.config.AxisHelper.x.fontSize = to
      this.viz.config.AxisHelper.y.fontSize = to

      this.viz.config.TooltipBehavior.fontSize = to
      this.updateChart()
    },
    palette(to) {
      if (
        this.viz.config.ItemsHelper.colorScale &&
        this.viz.config.ItemsHelper.colorScale === 'single'
      ) {
        if (this.type === 'linechart' && this.selection.length) {
          this.viz.config.ItemsHelper.colorDict = buildColorDict(
            colorPalettes[to].categorical,
            this.selection
          )
        } else {
          this.viz.config.ItemsHelper.colorDict = colorPalettes[to].single
        }
      } else {
        this.viz.config.ItemsHelper.colorDict = colorPalettes[to].scale
      }

      this.viz.config.colorMode = this.palette

      this.updateChart()
    },
    selection(to, from) {
      this.viz.chart.selection = to
      if (this.type === 'linechart') {
        if (to.length) {
          // Reuse current colorDict only when selection is already filled
          const currentColorDict =
            to.length > 1 || to.length < from.length ? this.viz.config.ItemsHelper.colorDict : {}
          this.viz.config.ItemsHelper.colorScale = 'categorical'
          this.viz.config.ItemsHelper.colorDict = buildColorDict(
            colorPalettes[this.palette].categorical,
            to,
            currentColorDict
          )
          this.viz.chart.getLegendData = () => {
            if (this.showFullLables) {
              return this.selection.map(d => ({ ...d, id: d.labels.en }))
            }
            return this.selection
          }
        } else {
          this.viz.config.ItemsHelper.colorScale = 'single'
          this.viz.config.ItemsHelper.colorDict = colorPalettes[this.palette].single
          delete this.viz.chart.getLegendData
        }

        this.updateChart()
      } else {
        this.viz.chart._updateSelection()
        if (this.type === 'heatmap') this.updateChart()
      }
    },
  },
  mounted() {
    this.viz = new this.chartClassName(this.$refs.vizRoot)
    window.currentChart = this
    const self = this
    let myConfig = {
      isMobile: this.isMobile,
      AxisHelper: {
        y: {
          side: 'left',
          unit: this.units.y?.label,
          domain: this.yDomain,
          valueFormat: this.valueFormat('y'),
        },
        x: { unit: this.units.x?.label },
        fontSize: this.isThumbnail ? 1 : this.fontSize,
        zoom:
          this.type === 'barchart' &&
          this.viz.$chartRoot.clientWidth / this.data.length < 20 &&
          !(this.isThumbnail || this.isPrintable),
        onZoom: () => this.$refs.swipeDisclaimer.classList.remove('visible'),
      },
      TooltipBehavior: {
        fontSize: this.fontSize,
        enableTooltips: !(this.isThumbnail || this.isPrintable),
      },
      SelectableHelper: {
        enableSelection: {
          value: !this.isThumbnail,
        },
      },
      LegendBehavior: {
        showLegend: !this.isThumbnail,
      },
      skin: 'light', // no entiendo porque no es el defualt...
      colorMode: this.palette,
    }
    // const isFlipped = this.isFlipped
    if (this.type === 'barchart') {
      // myConfig.AxisHelper.x.tickOrientation = 'skewed'
      myConfig.AxisHelper.x.tickOrientation = this.$store.state.query.fullLabel
        ? 'vertical'
        : 'skewed'
      myConfig.AxisHelper.x.customWrapMax = this.$store.state.query.fullLabel ? 120 : null
      // myConfig.AxisHelper.x.tickWrap = this.$store.state.query.fullLabel ? 'wrap' : 'none'
      myConfig.ItemsHelper = {
        colorScale: 'single',
        colorDict: colorPalettes[this.palette].single,
      }
      myConfig.TooltipBehavior.persistent = true
      myConfig.TooltipBehavior.allowMouseEvents = this.lollipopOpts.length == 0
      myConfig.TooltipBehavior.beforeUpdate = function(datum) {
        if (datum) self.$emit('focus', datum.id)
      }
      this.viz.chart.getLegendData = () => {
        if (!this.$store.state.query.lollipopOpts.length) return []
        const data = [
          {
            id: 'main',
            label: this.data.lollipopInfo.mainOption.labels.en,
          },
        ].concat(
          this.lollipopOpts.map(d => {
            return { ...d, label: d.labels[this.lng] }
          })
        )
        data.type = 'lollipop'
        return data
      }

      const self = this
      const prototypeFormatValue = this.viz.chart.formatValue
      const lastYear = this.data.slice(1).reduce((year, row) => {
        return year > row[this.data.fields._DATE_] ? year : row[this.data.fields._DATE_]
      }, 0)
      this.viz.chart.formatValue = function(value, onAxis) {
        if (onAxis) {
          if (onAxis.toLowerCase() === 'x') {
            const candidate = this.data.find(d => d.id.replace(/--.*/, '') === value)
            const labelField = self.showFullLables ? 'label' : 'id'
            let label = candidate[labelField]
            if (self.lastDates && candidate.year < lastYear) {
              label += '*'
            }
            return label
          }
        }
        return prototypeFormatValue.call(this, value, onAxis)
      }
    } else if (this.type === 'linechart') {
      myConfig.showPoints = !this.isThumbnail
      myConfig.AxisHelper.x.valueFormat = '[D]%Y'
      myConfig.AxisHelper.x.tickOrientation = 'skewed'

      myConfig.curve = 'sharp'
      if (this.selection.length) {
        myConfig.ItemsHelper = {
          colorScale: 'categorical',
          colorDict: buildColorDict(colorPalettes[this.palette].categorical, this.selection),
        }
        this.viz.chart.getLegendData = () => {
          if (this.showFullLables) {
            return this.selection.map(d => ({ ...d, id: d.labels.en }))
          }
          return this.selection
        }
      } else {
        myConfig.ItemsHelper = {
          colorScale: 'single',
          colorDict: colorPalettes[this.palette].single,
        }
      }

      const prototypeFormatValue = this.viz.chart.formatValue
      this.viz.chart.formatValue = function(value, onAxis) {
        if (onAxis.toLowerCase() === 'x') {
          return encodeDate(value)
        }

        return prototypeFormatValue.call(this, value, onAxis)
      }
    } else if (this.type === 'heatmap') {
      myConfig.AxisHelper.x.tickOrientation = this.showFullLables ? 'vertical' : 'skewed'
      myConfig.AxisHelper.y.unit = ' '
      myConfig.AxisHelper.x.unit = ' '
      myConfig.unit = this.units.y?.label
      myConfig.AxisHelper.x.customWrapMax = this.$store.state.query.fullLabel ? 120 : null

      myConfig.AxisHelper.y.columnsOrder = this.isNDSC
        ? {
            Access: 0,
            Use: 1,
            Innovation: 2,
            Jobs: 3,
            Society: 4,
            Trust: 5,
            'Market openness': 6,
          }
        : null
      myConfig.ItemsHelper = {
        colorScale: 'sequential2',
        colorDict: colorPalettes[this.palette].scale,
      }

      const self = this
      const prototypeFormatValue = this.viz.chart.formatValue
      this.viz.chart.formatValue = function(value, onAxis) {
        if (onAxis) {
          if (onAxis.toLowerCase() === 'x') {
            const candidate = this.data.find(d => d.id.replace(/--.*/, '') === value)
            if (candidate) {
              const labelField = self.showFullLables ? 'label' : 'id'
              return candidate[labelField].replace(/--.*/, '')
            }
            return ''
          } else if (onAxis.toLowerCase() === 'y') {
            if (self.isNDSC) return value
            else return encodeDate(value)
          }
        }
        return prototypeFormatValue.call(this, value, onAxis)
      }

      this.viz.chart.formatLegendValue = value => {
        return this.viz.chart.valueWithUnit(value, 'y_1')
      }
    } else if (this.type === 'choropleth') {
      const geojson = require('@/assets/geojson/World_Countries-ISO3.simplified.topo.json')

      if (geojson.type == 'FeatureCollection') {
        myConfig.features = geojson.features
      } else if (geojson.type == 'Topology') {
        myConfig.features = require('topojson-client').feature(geojson, 'collection').features
      } else {
        throw new Error('Unrecognized geographical JSON standard')
      }
      const isoCodes = Object.fromEntries(
        myConfig.features
          .filter(f => f.properties.ISO3_CODE)
          .map(f => [f.properties.ISO3_CODE, true])
      )
      this.$emit('isocodes', isoCodes)

      myConfig.featureIdProperty = 'ISO3_CODE'
      myConfig.featureLabelProperty = 'NAME_EN'
      myConfig.TooltipBehavior.onCentroid = true

      myConfig.ItemsHelper = {
        colorScale: 'sequential2',
        colorDict: colorPalettes[this.palette].scale,
      }

      myConfig.showZoomControls = !(this.isPrintable || this.isThumbnail)

      this.viz.chart.formatLegendValue = value => {
        return this.viz.chart.formatValue(value, 'y')
      }
    } else if (this.type === 'scatterplot') {
      myConfig.AxisHelper.margin = { right: this.units.x ? 70 : 20 }
      myConfig.AxisHelper.x.valueFormat = this.valueFormat('x')
      myConfig.AxisHelper.y.valueFormat = this.valueFormat('y')
      myConfig.AxisHelper.z = { unit: this.units.z?.label, valueFormat: this.valueFormat('z') }
      myConfig.sizeAttr = this.data.sizeAttr
      myConfig.showLabels = true
      const colorDict = colorPalettes[this.palette].single
      myConfig.ItemsHelper = {
        colorScale: 'categorical',
        colorDict: { 'Current time period': colorDict.all, 'Less-recent time period': '#ccc' },
        colorKey: 'group',
      }
      myConfig.AxisHelper.x.tickMatchData = false
      this.viz.chart.setSelectedFunc = function(sel) {
        sel
          .select('text')
          .transition()
          .duration(300)
          .style('font-size', self.fontSize + 'px')
          .style('opacity', '1')
      }
      this.viz.chart.unsetSelectedFunc = function(sel) {
        sel
          .select('text')
          .transition()
          .duration(200)
          .style('font-size', '0px')
          .style('opacity', '0')
      }
      let self = this
      myConfig.formatScatterLegend = function(item) {
        return self.showFullLables ? item.label : item.id
      }
    }

    if (this.viz.chart.getLegendData) {
      const _getLegendData = this.viz.chart.getLegendData.bind(this.viz.chart)
      this.viz.chart.getLegendData = function() {
        var data = _getLegendData()
        if (data) {
          data.lastDates = self.lastDates
        } else {
          data = self.viz.chart.data
          data.lastDates = self.lastDates
        }

        return data
      }
    }

    myConfig.ItemsHelper.transition = !(this.isPrintable || this.isThumbnail)
    myConfig = mergeConfig(myConfig, extractValues(this.viz._getConfig()))

    if (myConfig.SelectableHelper.enableSelection) {
      this.viz.$chartRoot.addEventListener('update:selection', ev => {
        this.$emit('update:selection', ev.detail)
      })
      this.viz.chart.selection = this.selection
    }
    this.viz.config = myConfig
    if (this.isPrintable || this.isThumbnail) {
      this.viz.chart.$root.classList.add('is-printable')
    }

    fontCheck().then(() => {
      this.updateChart()
    })

    if (this.viz.config.AxisHelper.zoom) {
      this.$refs.swipeDisclaimer.classList.add('visible')
    }
  },
  methods: {
    getRangeMax(axis) {
      if (axis && !this.data.range[axis] && axis !== 'y') return null
      if (Array.isArray(this.data.range)) {
        return Math.max.apply(null, this.data.range)
      } else if (typeof this.data.range === 'object') {
        return Math.max.apply(null, this.data.range[axis])
      } else return null
    },
    getRangeMin(axis) {
      if (axis && !this.data.range[axis] && axis !== 'y') return null
      if (Array.isArray(this.data.range)) {
        return Math.min.apply(null, this.data.range)
      } else if (typeof this.data.range === 'object') {
        return Math.min.apply(null, this.data.range[axis])
      } else return null
    },
    getRange(axis) {
      if (axis && !this.data.range[axis] && axis !== 'y') return null
      if (Array.isArray(this.data.range)) {
        return this.data.range[1] - this.data.range[0]
      } else if (typeof this.data.range === 'object') {
        return this.data.range[axis][1] - this.data.range[axis][0]
      } else {
        return null
      }
    },
    valueFormat(axis) {
      let format

      if (this.getRange(axis) <= 1 && this.getRangeMax(axis) <= 1) {
        format = ['[N]0.00', '[N]0.000']
      } else if (
        this.getRange(axis) > 10 &&
        this.getRange(axis) < 100 &&
        ((this.units && this.units[axis || 'y']?.id === '%') ||
          (this.units && this.units[axis || 'y']) === undefined)
      ) {
        format = ['[N]0', '[N]0.00']
      } else if (this.getRangeMax(axis) > 1e12) {
        format = [
          '[A]{"precision":1,"measurement":"trillion"}',
          '[A]{"precision":2,"measurement":"trillion"}',
        ]
      } else if (this.getRangeMax(axis) > 1e9) {
        format = [
          '[A]{"precision":1,"measurement":"billion"}',
          '[A]{"precision":2,"measurement":"billion"}',
        ]
      } else if (this.getRangeMax(axis) > 1e6) {
        format = [
          '[A]{"precision":1,"measurement":"million"}',
          '[A]{"precision":2,"measurement":"million"}',
        ]
      } else if (this.getRangeMax(axis) > 1e3) {
        format = ['[A]{"precision":1,"measurement":"thousand"}', '[N]0,0.00']
      } else {
        format = ['[N]0.0a', '[N]0,0.00']
      }

      if (this.type === 'heatmap') {
        if (format.length === 1) {
          format.unshift('[N]0')
        } else {
          format[0] = '[N]0'
        }
      }

      return format
    },
    updateChart() {
      this.viz.data = this.data
    },
    exportSVG() {
      return this.viz.getSVGSource()
    },
    downloadSVG() {
      return this.viz.getSVGURL()
    },
  },
}
</script>

<style lang="stylus">
.chart {
  display: flex;
  flex-direction: column;

  .dates-legend {
    m-font-size(11, 16);
    color: $dark-gray;
    font-family: 'Caecilia LT Pro';

    .mobile & {
      +m-breakpoint(landscape) {
        font-size: 0.7em;
        line-height: 1.35;
        margin-top: 0px !important;
      }
    }
  }

  .zoom-buttons {
    margin-right: vw(-50px);

    .mobile & {
      margin-right: 0;
    }

    .z-btn {
      color: #828282;

      &:hover {
        background-color: $light-gray !important;
      }

      .mobile & {
        width: 26px !important;
        height: 26px !important;
        margin: 2px !important;
      }
    }
  }

  &[layout='print'] {
    .chart__time-header {
      position: relative;
    }

    .print-layout {
      display: flex;

      h4 {
        color: $black;
      }

      a {
        color: #0000EE;
      }
    }
  }

  &[layout='thumbnail'] {
    .unit-group {
      display: none;
    }

    .chart__time-header {
      display: none;
    }
  }

  &[layout='embedded'] {
    .chart__time-header {
      position: relative;
    }

    .chart__time-header > p {
      .mobile & {
        padding-left: 0;
      }
    }
  }

  .print-layout {
    display: none;
  }

  .viz-root {
    height: 100%;
    width: 100%;
    margin-top: 20px;

    &.scatterplot {
      margin-top: 35px;

      .mobile & {
        margin-top: 25px;
      }
    }

    .mobile & {
      margin-top: 0px;
    }

    &.barchart, &.choropleth {
      margin-top: 5px;
    }

    .tooltip {
      .icon {
        width: 25px;
        height: 25px;
        background-repeat: no-repeat;
        background-size: contain;
        background-position: center;
      }

      .content {
        box-shadow: 0 4px 4px rgba(#000, 0.25);
        color: $dark-gray;
        font-family: 'Bernina Sans', 'Helvetica Neue', Helvetica, Arial, serif;
        min-width: vw(200px);
        padding: vw(10px);

        .mobile & {
          line-height: 1.45;

          +m-breakpoint(landscape) {
            padding: vh(10px);
            min-width: vw(100px);
          }
        }

        .important, .value {
          color: $black;
          margin-bottom: vw(5px);
        }

        .value {
          white-space: nowrap;
        }

        td:not(.important) {
          margin-right: 1em;
        }

        td.important {
          text-align: right;
        }

        .evolutive-root {
          display: flex;
          justify-content: center;
          margin: vw(10px) vw(15px);

          .mobile & {
            +m-breakpoint(landscape) {
              margin: vh(10px) vw(10px);
            }
          }
        }

        .content-wrapper {
          max-width: 300px;
          word-wrap: break-word;

          .mobile & {
            max-width: 220px;
          }

          .rows {
            display: block;
          }

          .row {
            display: flex;
            width: 100%;

            &:not(:first-child) {
              margin-top: 0.5em;
            }

            p {
              font-size: 1em;
            }

            table {
              td {
                padding: 0.2em 0.5em;
              }
            }
          }

          .columns {
            display: flex;
          }

          .column {
            display: flex;
            flex-direction: column;
            line-height: 1.2;

            &:first-child {
              flex: 1 1 0%;
              flex-direction: column;
              font-family: 'Bernina Sans', 'Helvetica Neue', Helvetica, Arial, serif;
              justify-content: center;
              margin-right: 3em;

              .mobile & {
                line-height: 1.3;
              }

              .important {
                font-family: 'Caecilia LT Pro';
                margin-bottom: vh(2px);

                .mobile & {
                  line-height: 1.3;
                }
              }
            }

            &:not(:first-child) {
              align-items: center;

              .important {
                font-family: 'Caecilia LT Pro';
                font-weight: 700;

                .mobile & {
                  font-size: 0.8rem;
                  line-height: 1.45;
                }
              }
            }

            &.lollipop-label {
              display: block;
              line-height: 25px;
              text-align: start;
              width: calc(240px - 1em);
              margin-right: 1em;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }

          > a {
            color: $blue-2;
            display: block;
            margin: 0 auto;
            width: fit-content;
            font-size: 0.9em;

            &:after {
              content: "→";
              padding-left: 0.25em;
            }
          }

          p {
            margin: 0;
            padding: 0;
          }
        }
      }
    }
  }

  .svg-btn {
    display: none;
  }

  >>> .legend-container {
    margin: 0 auto;

    .gradient {
      width: vw(200px);

      .gradient-rect {
        width: 100%;
      }
    }
  }

  footer {
    color: $black;
    margin-bottom: vh(50px);

    h4 {
      margin-bottom: 0em;
    }

    section.left {
      flex: 1;

      .disclaimers {
        display: block;

        .disclaimer {
          margin: 0.5em 0;

          &:last-child {
            margin-bottom: 2em;
          }

          &.last-dates > span {
            font-weight: bold;
            font-size: 1.4em;
            vertical-align: sub;
            vertical-align: -webkit-baseline-middle;
          }
        }
      }

      .annotations p {
        font-size: 0.9em;
        line-height: 1.4;
        margin: 0.5em 0;

        a {
          color: blue;
          margin-left: 0.5em;
          text-decoration: underline;
        }
      }
    }

    section.right {
      margin-top: auto;
      margin-bottom: 2em;
      padding: 0 2em;
      display: flex;
      flex-direction: column;
      align-items: center;

      picture {
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: left;

        &:not(:first-child) {
          padding-left: 1em;

          > h2 {
            margin-bottom: 0px;
          }
        }

        > h2 {
          color: #004b78;
          line-height: 1;
          margin-top: 0;
          margin-left: 0.5em;
          width: 160px;
        }
      }

      img.logo {
        margin: 0 auto;
        height: auto;
        width: 65px;
        margin-left: 40px;

        &.goingdigital {
          margin-left: 48px;
        }

        &.datakitchen {
          margin-left: 64px;
          margin-bottom: 1rem;
        }

        &.external {
          width: 90px;
        }
      }
    }
  }
}

.chart__time-header {
  position: absolute;
  left: 0px;
  top: 0px;
  display: none

  > p {
    color: $black;
    margin: 0.25em 0;
    padding: 0 1.3em;
    text-indent: 0.5em;

    .mobile & {
      padding-left: 0.55em;
    }
  }
}

.chart__swipe-disclaimer {
  position: absolute;
  right: 0px;
  top: 50%;
  transform: translateY(-100%);
  width: 100px;
  opacity: 0.5;
  display: none;
  pointer-events: none;

  .mobile & {
    width: 50px;
    font-size: 0.6em;
  }

  &.visible {
    display: block;
  }

  > i {
    width: 40px;
    height: 40px;
    margin-left: -7px;
    display: block;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    background-image: url('../../assets/images/swipe-icon.png');

    .mobile & {
      margin-left: -5px;
      width: 30px;
      height: 30px;
    }
  }
}
</style>
